import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import TableComponent from "../../components/common/TableComponent";
import PaginationComponent from "../../components/PaginationComponent";
import Loading from "../../components/common/LoaderCommon";
import { Search, X } from "lucide-react";

import {
  useDownloadPurchaseReport,
  useInfiniteGetPurchaseEntryReport,
} from "../../service/reportsService";
import DownloadButton from "./DownloadFile";

function PurchaseReport() {
  const [searchKey, setSearchKey] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [debouncedVendorName] = useDebounce(vendorName, 500);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(0);

  const downloadMutation = useDownloadPurchaseReport();

  const { data, fetchNextPage, isLoading } = useInfiniteGetPurchaseEntryReport({
    pageSize: 10,
    pageNo: 1,
    purchaseNumber: searchKey,
    vendorName: debouncedVendorName,
    startDate: startDate,
    endDate: endDate,
  });

  const totalPages = Math.ceil((Number(data?.pages[0]?.totalPages) || 0) / 10);

  const purchaseData = data?.pages[0] || [];

  const purchaseColumns = useMemo(
    () => [
      { header: "Purchase Number", accessor: "purchaseNumber" },
      { header: "Vendor", accessor: "vendorName" },
      { header: "VATIN", accessor: "vendorVatin" },
      { header: "Purchase Date", accessor: "purchaseDate" },
      { header: "Items Count", accessor: "itemCount" },
      { header: "Total Amount", accessor: "totalAmount" },
      { header: "Amount Paid", accessor: "amountPaid" },
      { header: "Balance", accessor: "balanceAmount" },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex justify-between items-end flex-wrap gap-4">
          <div className="flex gap-4 flex-wrap items-end">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52627C]" />
                <input
                  type="text"
                  placeholder="Search purchase number"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF] w-[280px]"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Vendor Name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52627C]" />
                <input
                  type="text"
                  placeholder="Search vendor name"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF] w-[280px]"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
            {(searchKey || vendorName || startDate || endDate) && (
              <div className="flex flex-col gap-1 justify-end">
                <button
                  onClick={() => {
                    setSearchKey("");
                    setVendorName("");
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors"
                >
                  <X className="w-4 h-4" />
                  Reset
                </button>
              </div>
            )}
          </div>
          <DownloadButton
            endDate={endDate}
            startDate={startDate}
            vendorName={debouncedVendorName}
            mutation={downloadMutation}
          />
        </div>
      </div>

      {/* Table card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
        <div className="flex justify-between items-center border-b border-[#E8EEF9] py-3 px-6">
          <p className="text-sm text-[#52627C]">
            <span className="font-semibold text-[#21314A]">
              {purchaseData?.length}
            </span>{" "}
            Purchase entries found
          </p>
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={() => {
              if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
              }
            }}
          />
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <TableComponent
            caption="A list of your recent invoices."
            columns={purchaseColumns}
            data={purchaseData || []}
          />
        )}
      </div>
    </div>
  );
}

export default PurchaseReport;
